import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Copy, Edit, Play, Send, Pause, Archive, ChartBar, Search, Sparkles, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { VoiceLayout } from './VoiceLayout';
import { VOICE_AGENTS } from '@/data/voiceAgents';
import { usePageTitle } from '@/lib/usePageTitle';
import { cn } from '@/lib/utils';

const STATUS_TONE = {
  Active:            'bg-success/15 text-success border-success/40',
  'Pending Approval':'bg-warning/15 text-warning border-warning/40',
  Draft:             'bg-muted text-muted-foreground border-border',
  Paused:            'bg-info/15 text-info border-info/40',
  Archived:          'bg-muted/60 text-muted-foreground border-border',
};

export default function AgentLibrary() {
  usePageTitle('Agent Library');
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');

  const list = VOICE_AGENTS
    .filter((a) => filter === 'all' ? true : a.status === filter)
    .filter((a) => !q || (a.name + a.type + a.useCase).toLowerCase().includes(q.toLowerCase()));

  return (
    <VoiceLayout
      title="Agent library"
      subtitle="Every AI voice agent configured across the platform — voices, scripts, use-cases, owners and live performance at a glance."
      actions={(<Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate('/app/voice-agents/create')} data-testid="lib-create"><Plus className="h-4 w-4 mr-1.5" aria-hidden="true" />Create new agent</Button>)}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative w-full md:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input className="pl-9 h-9" placeholder="Search by name or use-case" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search agent library" />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap" role="tablist" aria-label="Filter by status">
          {['all', 'Active', 'Pending Approval', 'Draft', 'Paused', 'Archived'].map((s) => (
            <button key={s} role="tab" aria-selected={filter === s}
              onClick={() => setFilter(s)}
              className={cn('px-2.5 py-1.5 text-[11px] rounded-md border transition-colors',
                filter === s ? 'bg-primary/10 text-foreground border-primary/40' : 'border-border text-muted-foreground hover:text-foreground')}
              data-testid={`lib-filter-${s.toLowerCase().replace(' ','-')}`}
            >{s === 'all' ? 'All' : s}</button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {list.map((a) => (
          <article key={a.id} className="surface-card p-4 flex flex-col gap-3 hover:shadow-md transition-shadow" data-testid={`agent-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className={cn('h-11 w-11 rounded-xl text-[13px] font-semibold flex items-center justify-center',
                  a.color === 'rose'    && 'bg-rose-500/15    text-rose-600',
                  a.color === 'amber'   && 'bg-amber-500/15   text-amber-600',
                  a.color === 'emerald' && 'bg-emerald-500/15 text-emerald-600',
                  a.color === 'indigo'  && 'bg-indigo-500/15  text-indigo-600',
                  a.color === 'sky'     && 'bg-sky-500/15     text-sky-600',
                  a.color === 'violet'  && 'bg-violet-500/15  text-violet-600',
                )} aria-hidden="true">{a.initials}</div>
                <div>
                  <p className="font-semibold leading-tight">{a.name} <span className="text-muted-foreground font-normal">— {a.type}</span></p>
                  <p className="text-[11px] text-muted-foreground">{a.useCase}</p>
                </div>
              </div>
              <Badge className={cn('text-[10px] border', STATUS_TONE[a.status])}>{a.status}</Badge>
            </div>

            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="rounded-md bg-muted/40 p-2">
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Calls today</p>
                <p className="font-mono-num text-base font-medium">{a.callsToday}</p>
              </div>
              <div className="rounded-md bg-muted/40 p-2">
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Success</p>
                <p className="font-mono-num text-base font-medium">{a.successRate}%</p>
              </div>
              <div className="rounded-md bg-muted/40 p-2">
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground">Compliance</p>
                <p className="font-mono-num text-base font-medium inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-success" aria-hidden="true" />{a.complianceScore}</p>
              </div>
            </div>

            <div className="text-[11px] text-muted-foreground space-y-0.5">
              <p><span className="text-foreground">Voice:</span> {a.voice}</p>
              <p><span className="text-foreground">Dialect:</span> {a.dialect}</p>
              <p><span className="text-foreground">Owner:</span> {a.owner} · updated {a.lastUpdated}</p>
              <p><span className="text-foreground">Campaigns:</span> {a.assignedCampaigns.length ? a.assignedCampaigns.join(' · ') : <em className="text-muted-foreground/70">none yet</em>}</p>
            </div>

            <div className="flex items-center gap-1.5 mt-auto pt-2 border-t border-border flex-wrap">
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => navigate('/app/voice-agents/simulator')} data-testid={`agent-test-${a.id}`}><Play className="h-3 w-3 mr-1" aria-hidden="true" />Test</Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]"><Edit className="h-3 w-3 mr-1" aria-hidden="true" />Edit</Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]"><Copy className="h-3 w-3 mr-1" aria-hidden="true" />Duplicate</Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => navigate('/app/voice-agents/campaigns')}><Send className="h-3 w-3 mr-1" aria-hidden="true" />Assign</Button>
              <Button size="sm" variant="ghost" className="h-7 text-[11px] ml-auto"><ChartBar className="h-3 w-3 mr-1" aria-hidden="true" />Performance</Button>
            </div>
          </article>
        ))}
      </div>
    </VoiceLayout>
  );
}
